/**
 * Authentication context and hook
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, LoginRequest, RegisterRequest, UserRole } from '../types';
import { authService, webSocketService } from '../services';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => void;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  const login = async (credentials: LoginRequest): Promise<void> => {
    const token = await authService.login(credentials);
    await fetchCurrentUser();
    
    // Connect to WebSocket with the new token
    webSocketService.connect(token.access_token);
  };

  const register = async (data: RegisterRequest): Promise<void> => {
    const token = await authService.register(data);
    await fetchCurrentUser();
    
    // Connect to WebSocket with the new token
    webSocketService.connect(token.access_token);
  };

  const logout = useCallback((): void => {
    authService.logout();
    setUser(null);
    webSocketService.disconnect();
  }, []);

  const fetchCurrentUser = useCallback(async (): Promise<void> => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      logout();
    }
  }, [logout]);

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return user ? roles.some(role => role === user.role) : false;
  };

  const refresh = async (): Promise<void> => {
    if (authService.isAuthenticated()) {
      await fetchCurrentUser();
    }
  };

  // Initialize authentication state
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      
      if (authService.isAuthenticated()) {
        try {
          await fetchCurrentUser();
          
          // Connect to WebSocket if we have a valid token
          const token = authService.getStoredToken();
          if (token) {
            webSocketService.connect(token);
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          logout();
        }
      }
      
      setIsLoading(false);
    };

    initAuth();
  }, [fetchCurrentUser, logout]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    hasRole,
    hasAnyRole,
    refresh,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}